import { lazy, Suspense, useEffect, useRef, useState } from "react";
import { TopBar } from "./components/TopBar";
import { MobileModeBar } from "./components/MobileModeBar";
import { BuildView } from "./components/views/BuildView";
import { DesignView } from "./components/views/DesignView";
import { WallView } from "./components/views/WallView";
import { NUDGE_GESTURE_MS } from "./lib/history";
import { useDesign } from "./lib/store";
import { FlagsProvider } from "./components/FlagsProvider";
import { clampPoseInRoom, moveVertexById, resolveHostSegment } from "./lib/interiorWalls";
import { resolveFurniture } from "./lib/furniture/resolve";
import type { Opening } from "./lib/types";
import { exitSharedRoom, openRoom } from "./lib/openRoom";

// Overlays that are conditional and/or animation-heavy (framer-motion).
// Deferring them keeps their code out of the first-paint critical path;
// each renders nothing until its own condition is met anyway. (The three
// canvas views are kept as normal imports — the room is the landing's
// largest contentful paint, so splitting it behind a Suspense boundary
// measurably delayed it without helping the first paint, which the inline
// boot splash in index.html already handles.)
const Onboarding = lazy(() =>
  import("./components/Onboarding").then((m) => ({ default: m.Onboarding }))
);
const LibraryHighlight = lazy(() =>
  import("./components/LibraryHighlight").then((m) => ({ default: m.LibraryHighlight }))
);
const AcceptInvite = lazy(() =>
  import("./components/AcceptInvite").then((m) => ({ default: m.AcceptInvite }))
);
const DebugMultiplayerHUD = lazy(() =>
  import("./components/DebugMultiplayerHUD").then((m) => ({ default: m.DebugMultiplayerHUD }))
);
const DebugFurnitureGallery = lazy(() =>
  import("./components/DebugFurnitureGallery").then((m) => ({ default: m.DebugFurnitureGallery }))
);
import { subscribeRegistry } from "./lib/sharedRoomsRegistry";
import { usePresenceBridge } from "./lib/presenceBridge";
import {
  consumeCheckoutCancelParam,
  consumeCheckoutSuccessParam,
  hasCheckoutSuccessParam,
  subscribeActiveStripeSubscription,
} from "./lib/stripeCheckout";
import { pollPremiumUntilActive, refreshPremium } from "./lib/premium";
import { getCurrentUser, waitForAuthReady } from "./lib/auth";
import { PremiumActivatingBanner } from "./components/PremiumActivatingBanner";
import { CampaignLandingGate } from "./components/onboarding/CampaignLandingGate";
import { useResumeStartTogether } from "./lib/startTogetherController";
import type { AcceptShareInviteResult } from "./lib/sharedInvites";
import { TelemetryErrorBoundary, track } from "./lib/telemetry";
import { isRender3dOpen } from "./lib/render3dRequests";
import { FinishesHost } from "./components/finishes/FinishesHost";
import { installFinishesTelemetry } from "./lib/finishesTelemetry";

// Guided first-run surfaces — framer-motion-heavy and only ever shown to a
// subset of visitors, so they load on demand rather than in the entry bundle.
const JoinedCoachmark = lazy(() =>
  import("./components/onboarding/JoinedCoachmark").then((m) => ({ default: m.JoinedCoachmark }))
);
const GettingStartedChecklist = lazy(() =>
  import("./components/onboarding/GettingStartedChecklist").then((m) => ({
    default: m.GettingStartedChecklist,
  }))
);
const GlobalUpsell = lazy(() =>
  import("./components/GlobalUpsell").then((m) => ({ default: m.GlobalUpsell }))
);
const CollabIntro = lazy(() =>
  import("./components/onboarding/CollabIntro").then((m) => ({ default: m.CollabIntro }))
);
const FengShuiGuide = lazy(() =>
  import("./components/onboarding/FengShuiGuide").then((m) => ({ default: m.FengShuiGuide }))
);
const FengShuiLanding = lazy(() =>
  import("./components/onboarding/FengShuiLanding").then((m) => ({ default: m.FengShuiLanding }))
);
const Render3DGuide = lazy(() =>
  import("./components/onboarding/Render3DGuide").then((m) => ({ default: m.Render3DGuide }))
);
const Render3DLanding = lazy(() =>
  import("./components/onboarding/Render3DLanding").then((m) => ({ default: m.Render3DLanding }))
);
const RoomTemplateSheet = lazy(() =>
  import("./components/onboarding/RoomTemplateSheet").then((m) => ({ default: m.RoomTemplateSheet }))
);
const SaveRoomSheet = lazy(() =>
  import("./components/onboarding/SaveRoomSheet").then((m) => ({ default: m.SaveRoomSheet }))
);
const DebugEventsHUD = lazy(() =>
  import("./components/DebugEventsHUD").then((m) => ({ default: m.DebugEventsHUD }))
);
// The 3D walk-through host is tiny; it imports the three.js engine only
// when a walk-through actually opens.
const Render3DHost = lazy(() =>
  import("./components/render3d/Render3DHost").then((m) => ({ default: m.Render3DHost }))
);
const DebugRender3DGallery = lazy(() =>
  import("./components/DebugRender3DGallery").then((m) => ({ default: m.DebugRender3DGallery }))
);

/** Whether `target` is a place where the user is typing — we should keep
 *  arrow keys / Backspace working natively there instead of intercepting. */
function isEditableTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target instanceof HTMLInputElement) return true;
  if (target instanceof HTMLTextAreaElement) return true;
  if (target instanceof HTMLSelectElement) return true;
  return target.isContentEditable;
}

/**
 * Translate an arrow key into a (dx, dy) nudge in inches. Plain arrow keys
 * step by 1 inch (the smallest meaningful unit in the design's coord space —
 * basically "pixel-precise" in inches), Shift bumps that to a foot so users
 * can move things across the room quickly without giving up keyboard focus.
 *
 * Returns `null` if the key isn't an arrow.
 */
function arrowDelta(key: string, shift: boolean): { dx: number; dy: number } | null {
  const step = shift ? 12 : 1;
  switch (key) {
    case "ArrowLeft":
      return { dx: -step, dy: 0 };
    case "ArrowRight":
      return { dx: step, dy: 0 };
    case "ArrowUp":
      return { dx: 0, dy: -step };
    case "ArrowDown":
      return { dx: 0, dy: step };
    default:
      return null;
  }
}

/**
 * Nudge whichever item is currently selected by `(dx, dy)` inches. Returns
 * true iff something was actually moved (so the caller can `preventDefault`
 * to suppress the browser's default scroll on arrow keys).
 *
 * The handler reads from / writes through `useDesign.getState()` so it can
 * stay outside React without subscribing to every selection change. Each
 * view owns one kind of selection (see `setView` in the store), so we
 * dispatch on whatever is non-null.
 */
function nudgeSelection(dx: number, dy: number): boolean {
  const state = useDesign.getState();
  const { design } = state;

  // Design view: nudge selected furniture, clamping the footprint so it
  // can't poke through a wall — same invariant `handleFurnitureDragMove`
  // enforces during a mouse drag.
  if (state.selectedFurnitureId) {
    const f = design.furniture.find((f) => f.id === state.selectedFurnitureId);
    if (!f) return false;
    const next = clampPoseInRoom(
      { x: f.x + dx, y: f.y + dy },
      resolveFurniture(f).footprint,
      f.rotation,
      design.room
    );
    if (next.x === f.x && next.y === f.y) return false;
    state.updateFurniture(f.id, { x: next.x, y: next.y });
    return true;
  }

  // Wall view: ←/→ slides along the wall, ↑/↓ walks the mount up/down the
  // wall. Floor sits at the bottom of the elevation, so ↑ increases
  // `fromFloor`. Both axes are clamped to keep the item on the wall.
  if (state.selectedMountId) {
    const m = design.mounts.find((m) => m.id === state.selectedMountId);
    if (!m) return false;
    const host = resolveHostSegment(design.room, m);
    if (!host) return false;
    const wallLen = host.len;
    const halfW = m.width / 2;
    const halfH = m.height / 2;
    const nextAlong = Math.max(halfW, Math.min(wallLen - halfW, m.alongWall + dx));
    const nextFromFloor = Math.max(
      halfH,
      Math.min(design.room.ceilingHeight - halfH, m.fromFloor - dy)
    );
    if (nextAlong === m.alongWall && nextFromFloor === m.fromFloor) return false;
    state.updateMount(m.id, { alongWall: nextAlong, fromFloor: nextFromFloor });
    return true;
  }

  if (state.selectedOpeningId) {
    const o = design.openings.find((o) => o.id === state.selectedOpeningId);
    if (!o) return false;
    const host = resolveHostSegment(design.room, o);
    if (!host) return false;
    const wallLen = host.len;
    const halfW = o.width / 2;
    const halfH = o.height / 2;
    const nextAlong = Math.max(halfW, Math.min(wallLen - halfW, o.alongWall + dx));
    const patch: Partial<Opening> = {};
    let changed = false;
    if (nextAlong !== o.alongWall) {
      patch.alongWall = nextAlong;
      changed = true;
    }
    // Doors are floor-mounted — `fromFloor` is locked to height/2.
    if (o.kind !== "door" && dy !== 0) {
      const nextFromFloor = Math.max(
        halfH,
        Math.min(design.room.ceilingHeight - halfH, o.fromFloor - dy)
      );
      if (nextFromFloor !== o.fromFloor) {
        patch.fromFloor = nextFromFloor;
        changed = true;
      }
    }
    if (!changed) return false;
    state.updateOpening(o.id, patch);
    return true;
  }

  // Build view: nudge a single corner of the room polygon. We use the
  // "live" setter so arrow-spamming doesn't flood the undo stack — same
  // semantics as a vertex drag.
  if (state.selectedInteriorVertexId) {
    const id = state.selectedInteriorVertexId;
    const onOutline = (design.room.pointIds ?? []).indexOf(id);
    const current =
      onOutline >= 0
        ? design.room.points[onOutline]
        : design.room.interiorVertices?.find((v) => v.id === id);
    if (!current) return false;
    state.setRoomLive(
      moveVertexById(design.room, id, { x: current.x + dx, y: current.y + dy })
    );
    return true;
  }

  if (state.selectedRoomVertexIndex != null) {
    const i = state.selectedRoomVertexIndex;
    const points = design.room.points;
    const v = points[i];
    if (!v) return false;
    const nextPoints = points.map((p, idx) =>
      idx === i ? { x: p.x + dx, y: p.y + dy } : p
    );
    state.setRoomPointsLive(nextPoints);
    return true;
  }

  return false;
}

/**
 * Read URL params for shared-room routing without taking a full router
 * dependency. Watches `popstate` so back/forward and our internal
 * `pushState` calls (e.g. accepting an invite) re-resolve correctly.
 */
function useShareUrlParams(): {
  inviteId: string | null;
  sharedRoomId: string | null;
  consumeInviteParam: () => void;
} {
  const [params, setParams] = useState(() => readParams());

  useEffect(() => {
    function onPop() {
      setParams(readParams());
    }
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  function consumeInviteParam() {
    if (typeof window === "undefined") return;
    const url = new URL(window.location.href);
    url.searchParams.delete("invite");
    url.searchParams.delete("from");
    window.history.replaceState({}, "", url.toString());
    setParams(readParams());
  }

  return { ...params, consumeInviteParam };
}

function readParams(): { inviteId: string | null; sharedRoomId: string | null } {
  if (typeof window === "undefined") {
    return { inviteId: null, sharedRoomId: null };
  }
  const sp = new URLSearchParams(window.location.search);
  return {
    inviteId: sp.get("invite"),
    sharedRoomId: sp.get("shared"),
  };
}

export default function App() {
  const view = useDesign((s) => s.view);
  const undo = useDesign((s) => s.undo);
  const redo = useDesign((s) => s.redo);
  const removeFurniture = useDesign((s) => s.removeFurniture);
  const removeMount = useDesign((s) => s.removeMount);
  const removeOpening = useDesign((s) => s.removeOpening);
  const selectedFurnitureId = useDesign((s) => s.selectedFurnitureId);
  const selectedMountId = useDesign((s) => s.selectedMountId);
  const selectedOpeningId = useDesign((s) => s.selectedOpeningId);
  const nudgeGestureOpenRef = useRef(false);
  const nudgeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => installFinishesTelemetry(), []);

  function endNudgeGesture() {
    if (nudgeTimerRef.current != null) {
      clearTimeout(nudgeTimerRef.current);
      nudgeTimerRef.current = null;
    }
    if (nudgeGestureOpenRef.current) {
      useDesign.getState().endGesture();
      nudgeGestureOpenRef.current = false;
    }
  }

  function beginNudgeGesture() {
    if (!nudgeGestureOpenRef.current) {
      useDesign.getState().beginGesture();
      nudgeGestureOpenRef.current = true;
    }
  }

  function scheduleEndNudgeGesture() {
    if (nudgeTimerRef.current != null) clearTimeout(nudgeTimerRef.current);
    nudgeTimerRef.current = setTimeout(() => {
      nudgeTimerRef.current = null;
      endNudgeGesture();
    }, NUDGE_GESTURE_MS);
  }

  const { inviteId, sharedRoomId, consumeInviteParam } = useShareUrlParams();

  // Bridge selection + presence state into RTDB while we're in a shared room.
  usePresenceBridge();
  // Finish an Invite / Start together run that left for a redirect sign-in.
  useResumeStartTogether();

  // One-shot boot. Routes the initial entry through `openRoom` so the
  // data model — not the URL — decides whether to start multiplayer sync:
  //   - URL `?shared=<id>`  → explicit deep link, force shared.
  //   - Persisted `activeSharedRoomId` → resume the last shared session
  //     (the registry will downgrade us if membership was revoked).
  //   - Persisted `activeSavedRoomId` → registry-driven; if that saved
  //     room is also a shared room we're a member of, auto-upgrade.
  const bootedRef = useRef(false);
  useEffect(() => {
    if (bootedRef.current) return;
    bootedRef.current = true;
    const state = useDesign.getState();
    if (sharedRoomId) {
      void openRoom(sharedRoomId, { forceShared: true });
      return;
    }
    if (state.activeSharedRoomId) {
      void openRoom(state.activeSharedRoomId, { forceShared: true });
      return;
    }
    if (state.activeSavedRoomId) {
      // Don't pass a fallback design — the store already has the design
      // loaded from localStorage. If the registry says this id is a
      // shared room we're a member of, openRoom upgrades us; otherwise
      // we stay on the cached private snapshot.
      void openRoom(state.activeSavedRoomId);
    }
    // Mount-only boot — subsequent URL/registry changes are handled by
    // the effects below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // React to URL `?shared=` changes after boot — back/forward buttons,
  // pasted links, deep-link entries from other tabs. The URL is now a
  // *trigger* for entering shared mode and a *consequence* of being in
  // it (see `writeSharedUrlParam`/`clearSharedUrlParam` in openRoom);
  // the source of truth is `activeSharedRoomId` + the registry.
  //
  // We track the previous param so we only exit when the user explicitly
  // navigates away from `?shared=` (popstate). `openRoom` writes the
  // param via `replaceState`, which does not update this hook's state —
  // treating "param is null" as exit would tear down sessions we just
  // started from the library/inbox paths.
  const prevSharedRoomIdRef = useRef<string | null>(sharedRoomId);
  useEffect(() => {
    if (!bootedRef.current) return;
    const prev = prevSharedRoomIdRef.current;
    prevSharedRoomIdRef.current = sharedRoomId;
    if (sharedRoomId) {
      if (sharedRoomId === useDesign.getState().activeSharedRoomId) return;
      void openRoom(sharedRoomId, { forceShared: true });
    } else if (prev !== null) {
      exitSharedRoom();
    }
  }, [sharedRoomId]);

  // React to membership changes from the shared-rooms registry:
  //   - Newly-shared: a room I have loaded (saved or shared) appears in
  //     my inbox → upgrade to shared mode automatically. Handles the
  //     "open a previously-shared room from the library" bug and the
  //     "someone shares with me while I happen to have the room loaded"
  //     edge case.
  //   - Revoked: my active shared room disappears from the inbox →
  //     downgrade. Catches removal faster than waiting for the next
  //     Firestore write to fail with permission-denied.
  useEffect(() => {
    return subscribeRegistry((ids) => {
      const state = useDesign.getState();
      if (
        state.activeSharedRoomId &&
        !ids.has(state.activeSharedRoomId)
      ) {
        exitSharedRoom();
        return;
      }
      if (
        !state.activeSharedRoomId &&
        state.activeSavedRoomId &&
        ids.has(state.activeSavedRoomId)
      ) {
        void openRoom(state.activeSavedRoomId, { forceShared: true });
      }
    });
  }, []);

  // Stripe Checkout redirects back to the app with `?checkout=success` on
  // a completed purchase. The browser-side redirect and Stripe's webhook
  // to the `invertase/firestore-stripe-payments` extension race — the
  // redirect almost always wins, so the very first force-refresh of the
  // ID token tends to see a token *without* the `stripeRole` claim yet.
  //
  // We solve this with two complementary signals that both feed
  // `refreshPremium()`:
  //
  //   1. `subscribeActiveStripeSubscription` — `onSnapshot` against
  //      `customers/{uid}/subscriptions`. The extension writes the
  //      subscription doc in the same handler that stamps the claim,
  //      so this fires within milliseconds of the webhook finishing
  //      (the fast path).
  //   2. `pollPremiumUntilActive` — bounded backoff (~45s) over forced
  //      token refreshes. Provides the resilient fallback when the
  //      listener never fires (Firestore offline, permission edge
  //      case, extension misconfigured, etc.).
  //
  // While either signal is in flight, the `PremiumActivatingBanner`
  // shows a small "Activating your Premium subscription…" affordance so
  // the user gets a clear acknowledgement of the purchase even before
  // the claim lands.
  //
  // `activating` is seeded from the URL via the `useState` initializer
  // (a pure read — no side effects) so the banner is painted on the
  // very first render. The `useEffect` then performs the side effects
  // (strip the URL param, kick off polling + listener); flipping it
  // back to `false` happens inside the polling promise's `.finally`
  // callback, which is a callback context the React-hooks lint plugin
  // is happy with. No-op on cancel or a normal page load.
  //
  // Critical: the Stripe redirect is a full page load, so when this
  // effect runs Firebase has not yet finished restoring the persisted
  // user from IndexedDB. We `await waitForAuthReady()` before reading
  // `getCurrentUser()` for the listener path — without that wait, a
  // first-time upgrader on a fresh browser session would silently skip
  // the fast-path listener (no uid available yet) AND have polling
  // resolve to `signed-out` on the very first tick, leaving them
  // non-premium until they manually reload.
  const [activating, setActivating] = useState(() => hasCheckoutSuccessParam());
  useEffect(() => {
    if (consumeCheckoutCancelParam()) track("checkout_canceled");
    if (!consumeCheckoutSuccessParam()) return;
    const startedAt = Date.now();
    const abort = new AbortController();
    void (async () => {
      const user = (await waitForAuthReady()) ?? getCurrentUser();
      if (abort.signal.aborted) return;
      if (user) {
        subscribeActiveStripeSubscription(
          user.uid,
          () => {
            void refreshPremium();
          },
          { signal: abort.signal }
        );
      }
      // Polling runs unconditionally — even when no user is signed in
      // (degenerate but possible: user signed out in another tab while
      // Stripe Checkout was open) `pollPremiumUntilActive` will resolve
      // to `signed-out` on its first tick and dismiss the banner. The
      // happy path force-refreshes the ID token until the extension
      // stamps the claim or the schedule runs out.
      try {
        const final = await pollPremiumUntilActive({ signal: abort.signal });
        if (!abort.signal.aborted && final.status !== "signed-out") {
          track("premium_activated", {
            activation_ms: Date.now() - startedAt,
            timed_out: final.status !== "premium",
          });
        }
      } finally {
        setActivating(false);
      }
    })();
    return () => abort.abort();
  }, []);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (isRender3dOpen()) return;
      const isMeta = e.metaKey || e.ctrlKey;
      // Panels that own their keys (the Finishes sheet's option rows) keep
      // arrows and Delete; undo / redo still work from anywhere.
      if (!isMeta && (e.target as HTMLElement | null)?.closest?.("[data-own-keys]")) return;
      if (isMeta && (e.key.toLowerCase() === "z" || (e.key.toLowerCase() === "y" && !e.shiftKey))) {
        // A surface such as the furniture creator owns the shortcut while it
        // is open. Both listeners sit on window, so registration order can
        // run this one first; the attribute is the handoff.
        if (e.defaultPrevented || document.querySelector("[data-undo-scope]")) return;
        endNudgeGesture();
        e.preventDefault();
        if (e.key.toLowerCase() === "y" || e.shiftKey) redo();
        else undo();
        return;
      }
      if (isMeta && e.key.toLowerCase() === "c" && !e.shiftKey && !isEditableTarget(e.target)) {
        if (useDesign.getState().copySelection()) e.preventDefault();
        return;
      }
      if (isMeta && e.key.toLowerCase() === "v" && !e.shiftKey && !isEditableTarget(e.target)) {
        if (useDesign.getState().pasteClipboard()) e.preventDefault();
        return;
      }
      if ((e.key === "Delete" || e.key === "Backspace") && !isEditableTarget(e.target)) {
        // In "Edit modules" mode Delete removes a *module* (handled by the
        // module tray), never the whole sectional.
        if (useDesign.getState().editingModulesId) return;
        if (selectedFurnitureId) {
          e.preventDefault();
          removeFurniture(selectedFurnitureId);
        } else if (selectedMountId) {
          e.preventDefault();
          removeMount(selectedMountId);
        } else if (selectedOpeningId) {
          e.preventDefault();
          removeOpening(selectedOpeningId);
        }
        return;
      }
      // Arrow keys nudge the currently-selected item by 1" (or 1' with Shift).
      // Skip when the user is typing in a field — number inputs use arrow
      // keys natively for their increment behaviour.
      const delta = arrowDelta(e.key, e.shiftKey);
      if (delta && !isEditableTarget(e.target)) {
        beginNudgeGesture();
        if (nudgeSelection(delta.dx, delta.dy)) {
          e.preventDefault();
        }
      }
    }
    function onKeyUp(e: KeyboardEvent) {
      if (isRender3dOpen()) return;
      if (!arrowDelta(e.key, e.shiftKey)) return;
      if (isEditableTarget(e.target)) return;
      scheduleEndNudgeGesture();
    }
    window.addEventListener("keydown", onKey);
    window.addEventListener("keyup", onKeyUp);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("keyup", onKeyUp);
      endNudgeGesture();
    };
  }, [undo, redo, removeFurniture, removeMount, removeOpening, selectedFurnitureId, selectedMountId, selectedOpeningId]);

  // Invitee-side hint after joining: who's in the room and that edits are
  // live. `undefined` = never joined this session, `null` = dismissed.
  const [joinedOwner, setJoinedOwner] = useState<string | null | undefined>(undefined);

  function handleInviteAccepted(roomId: string, meta: AcceptShareInviteResult) {
    // Drop the `?invite=` param so AcceptInvite stops rendering; the
    // `?shared=` URL is set as a side effect of `openRoom` succeeding.
    consumeInviteParam();
    void openRoom(roomId, { forceShared: true }).then((result) => {
      if (result === "shared") setJoinedOwner(meta.ownerDisplayName ?? "");
    });
  }

  return (
    <TelemetryErrorBoundary fallback={<AppCrashScreen />}>
      {/* Paid-social landings may be routed into the collaboration first
          run before the tree mounts (see CampaignLandingGate). Every other
          visitor passes straight through. */}
      <CampaignLandingGate>
      <FlagsProvider>
        {/* Root uses `dvh` so the layout follows iOS Safari's dynamic
            viewport (URL bar collapse/expand). The `h-full` fallback keeps
            older engines working. `overflow-hidden` keeps the canvas as
            the only scrollable surface. */}
        <div className="relative h-full min-h-[100dvh] w-full overflow-hidden bg-ink-50">
          {/* Canvas/view fills the viewport, after the landscape mode rail. */}
          <div className="absolute inset-y-0 right-0 left-[var(--rail-w)]">
            {view === "build" && <BuildView />}
            {view === "design" && <DesignView />}
            {view === "wall" && <WallView />}
          </div>
          {/* TopBar floats above the canvas with a translucent backdrop. */}
          <TopBar />
          <MobileModeBar />
          <PremiumActivatingBanner visible={activating} />
          {/* All of the below render nothing until their own state turns on;
              wrapping them in one null-fallback Suspense lets their chunks
              load lazily without ever flashing a spinner. */}
          <Suspense fallback={null}>
            <Onboarding />
            <LibraryHighlight />
            {inviteId && (
              <AcceptInvite
                inviteId={inviteId}
                onAccepted={handleInviteAccepted}
                onCancel={consumeInviteParam}
              />
            )}
            {joinedOwner !== undefined && joinedOwner !== null && (
              <JoinedCoachmark
                ownerName={joinedOwner}
                onDismiss={() => setJoinedOwner(null)}
              />
            )}
            <GettingStartedChecklist />
            <CollabIntro />
            <FengShuiGuide />
            <FengShuiLanding />
            <Render3DGuide />
            <Render3DLanding />
            <RoomTemplateSheet />
            <SaveRoomSheet />
            <GlobalUpsell />
            <Render3DHost />
            <FinishesHost />
            <DebugMultiplayerHUD />
            <DebugEventsHUD />
            <DebugFurnitureGallery />
            <DebugRender3DGallery />
          </Suspense>
        </div>
      </FlagsProvider>
      </CampaignLandingGate>
    </TelemetryErrorBoundary>
  );
}

/**
 * Last-resort fallback rendered when a React render throws. The Sentry
 * ErrorBoundary has already captured the exception; this just gives
 * the user a way to recover (reload) instead of staring at a blank page.
 */
function AppCrashScreen() {
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-ink-50 px-6">
      <div className="w-full max-w-md rounded-3xl bg-white ring-1 ring-ink-200 shadow-xl p-6 text-center">
        <h2 className="text-lg font-semibold text-ink-900">
          Something went wrong
        </h2>
        <p className="mt-2 text-body-sm text-ink-500">
          Room Canvas hit an unexpected error and the screen needs to reload.
          Your saved rooms are safe.
        </p>
        <button
          type="button"
          onClick={() => window.location.reload()}
          className="mt-5 inline-flex items-center justify-center rounded-xl bg-ink-900 px-4 py-2 text-sm font-medium text-white hover:bg-ink-800"
        >
          Reload the page
        </button>
      </div>
    </div>
  );
}
